@let detail = detail$ | async;
@let status = detailStatus$ | async;
@let inMillion = displayInMillion$ | async;

<div class="dmv-viewer-page">

    <header class="dmv-viewer-header">
        <button mat-icon-button type="button" (click)="goBack()" aria-label="Back">
            <mat-icon>arrow_back</mat-icon>
        </button>
        <h1>Algorithmic DMV review</h1>
        @if (detail) {
            <span class="header-meta">
                Fund #{{ detail.job.fundId }} · valuation #{{ detail.job.fundValuationId }}
                · {{ detail.job.totalResultCount }} results
                · {{ detail.job.pendingReviewCount }} pending
                @if (detail.job.fundCurrency) {
                    · figures in {{ detail.job.fundCurrency }}{{ inMillion ? "m" : "" }}
                } @else if (inMillion) {
                    · figures in millions
                }
            </span>
            @let bulkAccepting = (bulkAccepting$ | async) ?? false;
            @let acceptableCount = pendingAcceptable(detail).length;
            @if (acceptableCount > 0) {
                <button mat-flat-button color="primary" type="button" class="accept-all-btn"
                        [disabled]="bulkAccepting"
                        (click)="acceptAll(detail)">
                    @if (bulkAccepting) { Applying… } @else { Accept all ({{ acceptableCount }}) }
                </button>
            }
        }
    </header>

    @if (status === "LOADING" && !detail) {
        <div class="placeholder">Loading…</div>
    } @else if (status === "ERROR") {
        <div class="placeholder placeholder--error">Couldn't load this job. It may have expired.</div>
    } @else if (detail && detail.results.length === 0) {
        <div class="placeholder">No results in this batch.</div>
    } @else if (detail) {

        @for (result of detail.results; track result.resultId) {
            @let pending = (isPending$(result.resultId) | async) ?? false;

            <article class="result-card"
                     [class.result-card--ineligible]="result.algorithmStatus === ALGO_STATUS.Ineligible"
                     [class.result-card--failed]="result.algorithmStatus === ALGO_STATUS.Failed"
                     [class.result-card--accepted]="result.reviewStatus === REVIEW_STATUS.Accepted"
                     [class.result-card--skipped]="result.reviewStatus === REVIEW_STATUS.Skipped">

                <header class="result-card-header">
                    <div class="result-card-title">
                        <h2>{{ result.assetName }}</h2>
                        <span class="result-chip">{{ cardChipLabel(result) }}</span>
                    </div>
                    <div class="result-card-actions">
                        @if (result.reviewStatus === REVIEW_STATUS.PendingReview) {
                            @if (result.algorithmStatus === ALGO_STATUS.Success) {
                                <button mat-flat-button color="primary" type="button"
                                        [disabled]="pending"
                                        (click)="accept(result)">
                                    @if (pending) { Saving… } @else { Accept }
                                </button>
                            }
                            <button mat-stroked-button type="button"
                                    [disabled]="pending"
                                    (click)="skip(result)">
                                Skip
                            </button>
                        }
                    </div>
                </header>

                @if (result.algorithmStatus === ALGO_STATUS.Ineligible) {
                    <div class="card-message card-message--neutral">
                        <mat-icon>block</mat-icon>
                        <span>{{ result.ineligibilityReason ?? "Asset is ineligible for the algorithm." }}</span>
                    </div>
                } @else if (result.algorithmStatus === ALGO_STATUS.Failed) {
                    <div class="card-message card-message--error">
                        <mat-icon>error_outline</mat-icon>
                        <span>{{ result.ineligibilityReason ?? "Algorithm failed for this asset." }}</span>
                    </div>
                } @else if (result.algorithmStatus === ALGO_STATUS.Success && result.scenarios) {
                    @let s = result.scenarios;

                    <section class="company-strip">
                        <div class="company-cell">
                            <div class="cell-label">Current NAV</div>
                            <div class="cell-value">{{ s.currentNav !== undefined ? (s.currentNav | decimalFormat:'currency') : '—' }}</div>
                        </div>
                        <div class="company-cell">
                            <div class="cell-label">Current Total TVPI</div>
                            <div class="cell-value">{{ s.currentTotalTvpi !== undefined ? (s.currentTotalTvpi | decimalFormat:'multiple') : '—' }}</div>
                        </div>
                        <div class="company-cell">
                            <div class="cell-label">Weighted bid</div>
                            <div class="cell-value">{{ s.weightedBid !== undefined ? (s.weightedBid | decimalFormat:'currency') : '—' }}</div>
                        </div>
                        <div class="company-cell">
                            <div class="cell-label">Discount to NAV</div>
                            <div class="cell-value">{{ s.discountToNav !== undefined ? (s.discountToNav | decimalFormat:'percent') : '—' }}</div>
                        </div>
                        <div class="company-cell">
                            <div class="cell-label">Modeled exit</div>
                            <div class="cell-value">{{ s.modeledExit ?? '—' }}</div>
                        </div>
                    </section>

                    <table class="scenarios-table">
                        <thead>
                            <tr>
                                <th scope="col">Case</th>
                                <th scope="col" class="num">Discount multiple</th>
                                <th scope="col" class="num">Exit cash flow</th>
                                <th scope="col" class="num">Primary TVPI</th>
                                <th scope="col" class="num">Secondary TVPI</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of scenarioRows(result); track row.case) {
                                <tr [class.scenario-row--base]="row.case === 'BASE'">
                                    <th scope="row">{{ row.case }}</th>
                                    <td class="num">{{ row.values.discountMultiple !== undefined ? (row.values.discountMultiple | decimalFormat:'multiple') : '—' }}</td>
                                    <td class="num">{{ row.values.exitCashflow !== undefined ? (row.values.exitCashflow | decimalFormat:'currency') : '—' }}</td>
                                    <td class="num">{{ row.values.primaryTvpi !== undefined ? (row.values.primaryTvpi | decimalFormat:'multiple') : '—' }}</td>
                                    <td class="num">{{ row.values.secondaryMoIC !== undefined ? (row.values.secondaryMoIC | decimalFormat:'multiple') : '—' }}</td>
                                </tr>
                            }
                        </tbody>
                    </table>
                }

                @if (result.agentStatus === AGENT_STATUS.Success) {
                    @if (result.agentNarrative; as n) {
                        <section class="agent-context agent-context--{{ agentTone(n) }}">
                            <header class="agent-context-head">
                                <mat-icon class="agent-context-icon">psychology</mat-icon>
                                <span class="agent-context-title">Context for review</span>
                                <span class="agent-tone-chip agent-tone-chip--{{ agentTone(n) }}">{{ agentTone(n) }}</span>
                                @if (n.confidence) {
                                    <span class="agent-confidence">confidence: {{ n.confidence | lowercase }}</span>
                                }
                            </header>

                            @if (n.value || n.timing || n.conclusion) {
                                <div class="agent-summary">
                                    @if (n.value) {
                                        <div class="agent-summary-row">
                                            <span class="agent-summary-key">Value</span>
                                            <span class="agent-summary-val">{{ n.value.verdict }}@if (n.value.severity) {<span class="agent-summary-qual"> ({{ humanize(n.value.severity) }})</span>}</span>
                                        </div>
                                    }
                                    @if (n.timing) {
                                        <div class="agent-summary-row">
                                            <span class="agent-summary-key">Timing</span>
                                            <span class="agent-summary-val">{{ n.timing.verdict }}@if (n.timing.pattern) {<span class="agent-summary-qual"> ({{ humanize(n.timing.pattern) }})</span>}</span>
                                        </div>
                                    }
                                    @if (n.conclusion && n.conclusion.discountBandLow !== undefined && n.conclusion.discountBandHigh !== undefined) {
                                        <div class="agent-summary-row">
                                            <span class="agent-summary-key">Band</span>
                                            <span class="agent-summary-val">{{ n.conclusion.discountBandLow | decimalFormat:'percent' }} to {{ n.conclusion.discountBandHigh | decimalFormat:'percent' }}</span>
                                        </div>
                                    }
                                </div>
                            }

                            @if (n.value || n.timing) {
                                <div class="agent-axes">
                                    @if (n.value) {
                                        <div class="agent-axis">
                                            <span class="agent-axis-label">Value — why</span>
                                            @if (n.value.notes && n.value.notes.length > 0) {
                                                <ul class="agent-axis-notes">
                                                    @for (note of n.value.notes; track note) { <li>{{ note }}</li> }
                                                </ul>
                                            }
                                            @if (n.value.reviewerAction) {
                                                <p class="agent-axis-action">→ {{ n.value.reviewerAction }}</p>
                                            }
                                        </div>
                                    }
                                    @if (n.timing) {
                                        <div class="agent-axis">
                                            <span class="agent-axis-label">Timing — why</span>
                                            @if (n.timing.notes && n.timing.notes.length > 0) {
                                                <ul class="agent-axis-notes">
                                                    @for (note of n.timing.notes; track note) { <li>{{ note }}</li> }
                                                </ul>
                                            }
                                            @if (n.timing.reviewerAction) {
                                                <p class="agent-axis-action">→ {{ n.timing.reviewerAction }}</p>
                                            }
                                        </div>
                                    }
                                </div>
                            }

                            @for (section of narrativeSections(n); track section.label) {
                                <div class="agent-section">
                                    <span class="agent-section-label">{{ section.label }}</span>
                                    <ul class="agent-section-list">
                                        @for (item of section.items; track item) { <li>{{ item }}</li> }
                                    </ul>
                                </div>
                            }

                            @if (n.diligenceQuestions && n.diligenceQuestions.length > 0) {
                                <div class="agent-section">
                                    <span class="agent-section-label">Questions for the GP</span>
                                    <ul class="agent-section-list">
                                        @for (q of n.diligenceQuestions; track q.question) {
                                            <li><strong>{{ q.topic }}:</strong> {{ q.question }}</li>
                                        }
                                    </ul>
                                </div>
                            }

                            @if (n.conclusion && n.conclusion.text) {
                                <div class="agent-conclusion">
                                    <span class="agent-conclusion-text">{{ n.conclusion.text }}</span>
                                </div>
                            }

                            @if (suggestionForm(result); as form) {
                                @if (suggestionPreview(result); as sug) {
                                    @if (!sug.material) {
                                        <div class="agent-suggestion agent-suggestion--agrees">
                                            <div class="agent-suggestion-head">
                                                <span class="agent-section-label">Agent review agrees with algorithmic approach</span>
                                            </div>
                                            @if (sug.rationale) {
                                                <p class="agent-suggestion-rationale">{{ sug.rationale }}</p>
                                            }
                                        </div>
                                    } @else {
                                    <div class="agent-suggestion" [formGroup]="form">
                                        <div class="agent-suggestion-head">
                                            <span class="agent-section-label">Suggested amendment</span>
                                            @if (sug.movedDirection) {
                                                <span class="agent-suggestion-chip">moved {{ sug.movedDirection }} — off crowded quarter</span>
                                            }
                                        </div>
                                        <table class="suggestion-table">
                                            <thead>
                                                <tr>
                                                    <th scope="col"></th>
                                                    <th scope="col" class="num">Algorithm</th>
                                                    <th scope="col" class="num">Suggested (editable)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr [class.suggestion-row--changed]="sug.baseChanged">
                                                    <th scope="row">BASE multiple</th>
                                                    <td class="num">{{ sug.algorithmBase !== undefined ? (sug.algorithmBase | decimalFormat:'multiple') : '—' }}</td>
                                                    <td class="num">
                                                        <input type="number" step="0.01" min="0" class="suggestion-input"
                                                               formControlName="baseMultiple" aria-label="Suggested BASE multiple"/>
                                                    </td>
                                                </tr>
                                                <tr class="suggestion-row--preview">
                                                    <th scope="row">LOW / HIGH preview</th>
                                                    <td class="num">
                                                        {{ (result.scenarios?.low?.discountMultiple ?? undefined) !== undefined ? (result.scenarios?.low?.discountMultiple | decimalFormat:'multiple') : '—' }}
                                                        /
                                                        {{ (result.scenarios?.high?.discountMultiple ?? undefined) !== undefined ? (result.scenarios?.high?.discountMultiple | decimalFormat:'multiple') : '—' }}
                                                    </td>
                                                    <td class="num">
                                                        {{ sug.previewLow !== undefined ? (sug.previewLow | decimalFormat:'multiple') : '—' }}
                                                        /
                                                        {{ sug.previewHigh !== undefined ? (sug.previewHigh | decimalFormat:'multiple') : '—' }}
                                                    </td>
                                                </tr>
                                                <tr [class.suggestion-row--changed]="sug.movedDirection">
                                                    <th scope="row">Exit date</th>
                                                    <td class="num">{{ sug.algorithmExit ?? '—' }}</td>
                                                    <td class="num">
                                                        <input type="date" class="suggestion-input"
                                                               formControlName="exitDate" aria-label="Suggested exit date"/>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        @if (sug.rationale) {
                                            <p class="agent-suggestion-rationale">{{ sug.rationale }}</p>
                                        }
                                        <p class="agent-suggestion-note">Edit the base multiple or exit date — LOW/HIGH preview updates live (the server re-derives the full DMV and snaps the date to quarter-end on apply).</p>
                                        <div class="agent-suggestion-actions">
                                            <button mat-flat-button color="primary" type="button"
                                                    [disabled]="form.invalid || pending"
                                                    (click)="acceptSuggestion(result)">
                                                @if (pending) { Saving… } @else { Accept suggestion }
                                            </button>
                                        </div>
                                    </div>
                                    }
                                }
                            }

                            @if (n.citations && n.citations.length > 0) {
                                <div class="agent-citations">
                                    @for (c of n.citations; track c.n) {
                                        <span class="agent-citation">[{{ c.n }}] {{ c.source }}</span>
                                    }
                                </div>
                            }
                        </section>
                    }
                } @else if (result.agentStatus === AGENT_STATUS.Failed) {
                    <div class="agent-context-note agent-context-note--error">
                        <mat-icon>error_outline</mat-icon>
                        <span>Agent review failed for this result — the algorithm result above is unaffected.</span>
                    </div>
                } @else if (result.agentStatus === AGENT_STATUS.InProgress || result.agentStatus === AGENT_STATUS.Pending) {
                    <div class="agent-context-note">
                        <mat-icon>hourglass_empty</mat-icon>
                        <span>Agent review in progress…</span>
                    </div>
                }
            </article>
        }
    }
</div>
